import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import ConversationList from "./ConversationList";
import ConversationThread from "./ConversationThread";
import { DepartmentsProvider } from "./DepartmentsContext";
import { CrmUpgradeBanner, CrmUpgradeModal } from "./CrmUpgradeCta";
import { useTenantRole } from "../../hooks/useTenantRole";
import { usePlanFeature } from "../../hooks/usePlanFeature";
import {
  isUnknownContact,
  getSessionIdentity,
  getSessionTags,
  getHistoryMessages,
  countByQueue,
  filterByQueue,
  uniqueTags,
  isFeatureNotIncluded,
} from "./messageCenterHelpers";
import styles from "./MessageCenter.module.css";

const POLL_INTERVAL_MS = 5000;

/** Lê as mensagens da resposta de /tenants/sessions/:id (contrato: `messages`). */
const readMessages = (data) => getHistoryMessages(data?.messages ?? data?.history);

/**
 * Funde na lista a sessão devolvida por uma ação (enviar, assumir/devolver
 * controle). Essas rotas respondem com a linha crua do banco: trazem `history`,
 * mas não o `lastMessage` derivado que a listagem entrega — sem tratar, o preview
 * do item sumiria até o próximo poll. Deriva `lastMessage` do `history` da
 * resposta e descarta o blob: a lista não o consome (a thread aberta vive em
 * `threadMessages`) e guardá-lo em estado só devolveria peso à memória.
 */
const mergeSessionUpdate = (previous, updated) => {
  const { history, ...rest } = updated ?? {};
  const last = getHistoryMessages(history).at(-1);
  return { ...rest, lastMessage: last ?? previous.lastMessage ?? null };
};

/**
 * Centro de Mensagens — inbox profissional de duas colunas.
 *
 * Estado e orquestração: fetch + polling das sessões, seleção, busca local,
 * filtro por canal, envio otimista e controle humano (assumir / devolver à IA).
 *
 * A listagem NÃO traz mais o histórico das conversas (pesava megabytes a cada
 * poll de 5s). As mensagens da conversa aberta são buscadas sob demanda em
 * GET /tenants/sessions/:id e vivem em `threadMessages`.
 *
 * Rotas REAIS (não inventar): GET /tenants/sessions, GET /tenants/sessions/:id,
 * POST /tenants/sessions/:id/message, PATCH /tenants/sessions/:id/release-control.
 */
export default function MessageCenter() {
  const navigate = useNavigate();
  // `memberId` = TenantMember.id do usuário logado, necessário para "Pegar
  // conversa" (atribuir a si mesmo via PATCH { assignedToId }).
  const { memberId } = useTenantRole();
  // Gate de plano: só o Profissional (`crmIncluded`) libera as ações de EQUIPE
  // (filas, setores, atribuição, tags, notas). O Básico mantém o inbox base
  // (ver conversa + responder + assumir/devolver controle) — nada disso é
  // gateado. `crmEnabled` é o único sinal usado para esconder/bloquear a UI.
  const { allowed: crmEnabled, upgradeUrl } = usePlanFeature("crm");

  // 403 FEATURE_NOT_INCLUDED que escape do gate de UI → abre o CTA de upgrade
  // (nunca um erro cru). Defesa em profundidade sobre as ações de equipe.
  const [crmUpgradeOpen, setCrmUpgradeOpen] = useState(false);
  // Callback estável para NotesPanel (evita refetch das notas a cada render).
  const openCrmUpgrade = useCallback(() => setCrmUpgradeOpen(true), []);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  // Filtro secundário por tag (F2.5). `null` = todas as tags.
  const [tagFilter, setTagFilter] = useState(null);
  // Fila ativa (filtro primário). Padrão: caixa de entrada.
  const [queue, setQueue] = useState("INBOX");

  // Mensagens da conversa aberta — buscadas sob demanda, fora da listagem.
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  // Espelha `selectedId` para checagem pós-await: a conversa aberta pode ter
  // mudado enquanto a request estava em voo.
  //
  // A atribuição é feita em render (não em `useEffect`) de propósito: o ref
  // precisa estar atualizado quando QUALQUER await pendente resolver, e um
  // efeito passivo só roda depois do commit — abriria justamente a janela de
  // staleness que estas guardas existem para fechar. É o mesmo padrão que o
  // `useRefCallback` no rodapé deste arquivo já usa.
  // Seguro aqui porque `selectedId` só muda em evento discreto (clique), nunca
  // dentro de transition/Suspense — logo não há render descartado que pudesse
  // deixar o ref com um valor que nunca commitou.
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  // Conversas com request de controle (assumir/devolver) em voo — para
  // desabilitar o botão e evitar duplo clique.
  const [controlPending, setControlPending] = useState(() => new Set());
  // Conversas com request de mudança de fila (finalizar/reabrir) em voo.
  const [queuePending, setQueuePending] = useState(() => new Set());
  // Conversas com request de atribuição (setor/atendente) em voo.
  const [assignPending, setAssignPending] = useState(() => new Set());
  // Conversas com request de atualização de tags (F2.5) em voo.
  const [tagsPending, setTagsPending] = useState(() => new Set());

  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useRefCallback((message) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  });

  // ── Fetch + polling ────────────────────────────────────────────────────────
  const fetchSessions = useRefCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get("/tenants/sessions");
      setSessions(Array.isArray(res.data) ? res.data : []);
      setError(false);
    } catch {
      if (!silent) setError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  });

  /**
   * Refresh silencioso da conversa aberta: sem skeleton e sem toast de erro —
   * em falha mantém as mensagens já carregadas em tela.
   */
  const refreshThread = useRefCallback(async (sessionId) => {
    try {
      const res = await api.get(`/tenants/sessions/${sessionId}`);
      // Descarta resposta obsoleta: o atendente já abriu outra conversa.
      if (selectedIdRef.current !== sessionId) return;
      setThreadMessages(readMessages(res.data));
    } catch {
      // Silencioso por definição — o poll tenta de novo em 5s.
    }
  });

  // Um tick do poll: atualiza a lista (agora leve, sem histórico) e só a
  // conversa aberta — nunca o histórico de todas as sessões.
  const pollTick = useRefCallback(() => {
    fetchSessions(true);
    if (selectedIdRef.current) refreshThread(selectedIdRef.current);
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return undefined;
    }

    fetchSessions(false);
    const interval = setInterval(pollTick, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
    // fetchSessions/pollTick são estáveis (useRefCallback); navigate é estável
    // no RR v7. O intervalo não recria a cada troca de conversa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ── Mensagens da conversa aberta (fetch dedicado ao selecionar) ────────────
  useEffect(() => {
    if (!selectedId) {
      setThreadMessages([]);
      return undefined;
    }

    // `cancelled` resolve a corrida A→B: ao trocar de conversa o cleanup roda
    // antes da resposta de A chegar, então a resposta atrasada de A é
    // descartada em vez de sobrescrever o chat de B.
    let cancelled = false;
    setThreadLoading(true);
    setThreadMessages([]);

    (async () => {
      try {
        const res = await api.get(`/tenants/sessions/${selectedId}`);
        if (cancelled) return;
        setThreadMessages(readMessages(res.data));
      } catch {
        if (cancelled) return;
        // Feed vazio + toast: a tela continua utilizável (header e composer).
        showToast("Falha ao carregar as mensagens desta conversa.");
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedId, showToast]);

  // ── Seleção derivada (sempre a partir da lista mais recente) ────────────────
  const selected = useMemo(
    () => sessions.find((s) => s.id === selectedId) ?? null,
    [sessions, selectedId],
  );

  // ── Busca + filtro de canal local (secundário, sem nova request) ────────────
  // Base compartilhada: tudo que passa no chip de canal + na busca, ANTES da
  // fila. Dela derivamos tanto os contadores por fila quanto a lista visível —
  // assim os números das abas somam exatamente o que apareceria ao trocar de
  // aba com o canal/busca atuais (contagem "facetada", sem descompasso).
  const filteredByChannelSearch = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (filter !== "all" && s.platform !== filter) return false;
      // Tag (F2.5): secundário, aplicado ANTES da contagem por fila — as abas
      // refletem o subconjunto da tag (facetado, como o filtro de canal).
      if (tagFilter && !getSessionTags(s).includes(tagFilter)) return false;
      if (!term) return true;
      const name = (
        isUnknownContact(s.clientName) ? "" : s.clientName
      ).toLowerCase();
      const identity = getSessionIdentity(s);
      // Só busca por telefone quando ele é visível (contato oculto/BSUID não é
      // encontrável por número — e o número sequer é exposto).
      const phone = (identity.phoneNumber || "").toLowerCase();
      const username = (identity.username || "").toLowerCase();
      return (
        name.includes(term) ||
        phone.includes(term) ||
        username.includes(term)
      );
    });
  }, [sessions, search, filter, tagFilter]);

  // Opções do filtro por tag: união de todas as tags das sessões carregadas
  // (sobre a lista completa, para não sumir opções ao filtrar por canal/busca).
  const availableTags = useMemo(() => uniqueTags(sessions), [sessions]);

  // Contadores das abas (fila primária) sobre a base canal+busca.
  const queueCounts = useMemo(
    () => countByQueue(filteredByChannelSearch),
    [filteredByChannelSearch],
  );

  // Lista final: base canal+busca restrita à aba ativa. Para o Básico (sem CRM)
  // as filas não existem e as abas ficam escondidas — então mostramos TODAS as
  // conversas da base, sem restringir por fila (senão o Básico só veria a INBOX,
  // regredindo o atendimento básico ao esconder WAITING/DONE).
  const visibleSessions = useMemo(
    () =>
      crmEnabled
        ? filterByQueue(filteredByChannelSearch, queue)
        : filteredByChannelSearch,
    [crmEnabled, filteredByChannelSearch, queue],
  );

  // ── Ações ───────────────────────────────────────────────────────────────────
  const handleSelect = (session) => {
    setSelectedId(session.id);
    setDraft("");
  };

  const markControlPending = (id, pending) => {
    setControlPending((prev) => {
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleAssumeControl = async () => {
    if (!selected) return;
    const id = selected.id;
    markControlPending(id, true);
    try {
      const res = await api.patch(`/tenants/sessions/${id}/assume-control`);
      // Fonte de verdade: a sessão retornada já vem com humanControlActive=true,
      // o que habilita o composer de forma real e persistida.
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? mergeSessionUpdate(s, res.data) : s)),
      );
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        "Falha ao assumir o controle da conversa.";
      showToast(msg);
    } finally {
      markControlPending(id, false);
    }
  };

  const handleReleaseControl = async () => {
    if (!selected) return;
    const id = selected.id;
    markControlPending(id, true);
    try {
      const res = await api.patch(`/tenants/sessions/${id}/release-control`);
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? mergeSessionUpdate(s, res.data) : s)),
      );
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        "Falha ao devolver o controle para a IA.";
      showToast(msg);
    } finally {
      markControlPending(id, false);
    }
  };

  const markQueuePending = (id, pending) => {
    setQueuePending((prev) => {
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  /**
   * Move a conversa aberta entre filas (finalizar → DONE, reabrir → INBOX).
   * Otimista via resposta: a sessão retornada já traz o novo `queueStatus`, então
   * o merge por id recalcula abas/contadores na hora — sem esperar o poll de 5s.
   * A thread não é afetada (vive em `threadMessages`); só muda de aba na lista.
   */
  const handleSetQueueStatus = async (queueStatus) => {
    if (!selected) return;
    const id = selected.id;
    markQueuePending(id, true);
    try {
      const res = await api.patch(
        `/tenants/sessions/${id}/queue-status`,
        { queueStatus },
      );
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? mergeSessionUpdate(s, res.data) : s)),
      );
    } catch (err) {
      if (isFeatureNotIncluded(err)) {
        setCrmUpgradeOpen(true);
        return;
      }
      const msg =
        err?.response?.data?.error || "Falha ao atualizar a fila da conversa.";
      showToast(msg);
    } finally {
      markQueuePending(id, false);
    }
  };

  const handleFinish = () => handleSetQueueStatus("DONE");
  const handleReopen = () => handleSetQueueStatus("INBOX");

  const markAssignPending = (id, pending) => {
    setAssignPending((prev) => {
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  /**
   * Atribui a conversa aberta (F2.4). Um único endpoint cobre todos os casos —
   * o corpo decide a semântica:
   *   { departmentId }           → round-robin no setor (backend escolhe o dono)
   *   { assignedToId }           → atribuição direta a um atendente
   *   { assignedToId: null }     → desatribui
   * Otimista via resposta: a sessão retornada já traz assignedToId/departmentId
   * e o novo queueStatus (atribuir move INBOX→WAITING), então o merge por id
   * reposiciona a conversa na aba certa sem esperar o poll de 5s.
   */
  const handleAssign = async (payload) => {
    if (!selected) return;
    const id = selected.id;
    markAssignPending(id, true);
    try {
      const res = await api.patch(`/tenants/sessions/${id}/assign`, payload);
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? mergeSessionUpdate(s, res.data) : s)),
      );
    } catch (err) {
      if (isFeatureNotIncluded(err)) {
        setCrmUpgradeOpen(true);
        return;
      }
      const code = err?.response?.data?.code;
      const msg =
        code === "DEPARTMENT_HAS_NO_MEMBERS"
          ? "Este setor não tem atendentes. Adicione membros ao setor antes de atribuir por ele."
          : err?.response?.data?.error || "Falha ao atribuir a conversa.";
      showToast(msg);
    } finally {
      markAssignPending(id, false);
    }
  };

  const markTagsPending = (id, pending) => {
    setTagsPending((prev) => {
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  /**
   * Substitui o array de tags da conversa aberta (F2.5).
   * `PATCH /tenants/sessions/:id/tags { tags }` SUBSTITUI o array inteiro.
   * Otimista de verdade: a UI reflete `nextTags` na hora (sem esperar o poll de
   * 5s); a resposta reconcilia com o array normalizado do servidor (trim/dedupe).
   * Em erro, reverte para as tags anteriores + toast.
   */
  const handleSetTags = async (nextTags) => {
    if (!selected) return;
    const id = selected.id;
    const previousTags = getSessionTags(selected);
    // Otimista: aplica já na lista (por id, seguro mesmo se trocar de conversa).
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, tags: nextTags } : s)),
    );
    markTagsPending(id, true);
    try {
      const res = await api.patch(`/tenants/sessions/${id}/tags`, {
        tags: nextTags,
      });
      // Fonte de verdade: tags já normalizadas pelo backend.
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? mergeSessionUpdate(s, res.data) : s)),
      );
    } catch (err) {
      // Reverte o otimismo ao estado anterior.
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, tags: previousTags } : s)),
      );
      if (isFeatureNotIncluded(err)) {
        setCrmUpgradeOpen(true);
        return;
      }
      const msg =
        err?.response?.data?.error || "Falha ao atualizar as tags da conversa.";
      showToast(msg);
    } finally {
      markTagsPending(id, false);
    }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !selected || sending) return;

    // Fixa a conversa-alvo: `selected` é a do momento do clique, e o atendente
    // pode abrir outra enquanto o POST está em voo.
    const sessionId = selected.id;

    setSending(true);
    try {
      const res = await api.post(
        `/tenants/sessions/${sessionId}/message`,
        { text },
      );
      // Atualizar a LISTA é sempre seguro: o merge é endereçado por id, então
      // independe de qual conversa está aberta agora.
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId ? mergeSessionUpdate(s, res.data) : s,
        ),
      );

      // Mesma guarda de `refreshThread`: se o atendente já trocou de conversa,
      // não escrever nada de escopo da conversa antiga. Sem isto, a thread de B
      // seria populada com o histórico de A sob o cabeçalho de B.
      // O `draft` também fica de fora: limpá-lo aqui apagaria o que o atendente
      // já começou a digitar em B.
      if (selectedIdRef.current !== sessionId) return;

      // A resposta traz a sessão já com a mensagem enviada; se algum dia deixar
      // de trazê-la, recarrega a thread em vez de sumir com o que está em tela.
      // (`refreshThread` reavalia a guarda por conta própria após o seu await.)
      const sent = readMessages(res.data);
      if (sent.length) setThreadMessages(sent);
      else refreshThread(sessionId);
      setDraft("");
    } catch (e) {
      const msg =
        e?.response?.data?.error || "Falha ao enviar mensagem. Tente novamente.";
      showToast(msg); // mensagem permanece no campo (draft não é limpo)
    } finally {
      setSending(false);
    }
  };

  const controlBusy = selected ? controlPending.has(selected.id) : false;
  const queueBusy = selected ? queuePending.has(selected.id) : false;
  const assignBusy = selected ? assignPending.has(selected.id) : false;
  const tagsBusy = selected ? tagsPending.has(selected.id) : false;

  // A thread lê as mensagens de `session.history`; a listagem não as traz mais,
  // então injetamos as que foram buscadas sob demanda. Mantém o contrato do
  // ConversationThread intacto ("a sessão carrega seu histórico").
  const selectedWithMessages = useMemo(
    () => (selected ? { ...selected, history: threadMessages } : null),
    [selected, threadMessages],
  );

  return (
    <DepartmentsProvider>
      <div className={styles.container}>
        {/* Básico: faixa de upgrade no topo do inbox (o atendimento base segue
            liberado abaixo). Profissional: nada aqui. */}
        {!crmEnabled && (
          <div className={styles.upgradeRow}>
            <CrmUpgradeBanner upgradeUrl={upgradeUrl} />
          </div>
        )}

        <div className={styles.columns}>
          <div
            className={`${styles.listColumn} ${selected ? styles.hiddenOnMobile : ""}`}
          >
            <ConversationList
              sessions={visibleSessions}
              loading={loading}
              error={error}
              search={search}
              onSearch={setSearch}
              filter={filter}
              onFilter={setFilter}
              queue={queue}
              queueCounts={queueCounts}
              onQueue={setQueue}
              availableTags={availableTags}
              tagFilter={tagFilter}
              onTagFilter={setTagFilter}
              selectedId={selectedId}
              onSelect={handleSelect}
              onRetry={() => fetchSessions(false)}
              crmEnabled={crmEnabled}
            />
          </div>

          <div
            className={`${styles.threadColumn} ${selected ? "" : styles.hiddenOnMobile}`}
          >
            <ConversationThread
              session={selectedWithMessages}
              loadingThread={threadLoading}
              onSend={handleSend}
              onAssumeControl={handleAssumeControl}
              onReleaseControl={handleReleaseControl}
              controlBusy={controlBusy}
              onFinish={handleFinish}
              onReopen={handleReopen}
              queueBusy={queueBusy}
              onAssign={handleAssign}
              assignBusy={assignBusy}
              onSetTags={handleSetTags}
              tagsBusy={tagsBusy}
              onNotesError={showToast}
              onFeatureBlocked={openCrmUpgrade}
              currentMemberId={memberId}
              sending={sending}
              draft={draft}
              onDraftChange={setDraft}
              onBack={() => setSelectedId(null)}
              crmEnabled={crmEnabled}
            />
          </div>
        </div>

        {toast && (
          <div className={styles.toast} role="alert">
            {toast}
          </div>
        )}

        <CrmUpgradeModal
          open={crmUpgradeOpen}
          onClose={() => setCrmUpgradeOpen(false)}
          upgradeUrl={upgradeUrl}
        />
      </div>
    </DepartmentsProvider>
  );
}

/**
 * Pequeno hook utilitário: retorna uma callback estável que sempre chama a
 * versão mais recente da função fornecida — evita recriar o efeito de polling.
 */
function useRefCallback(fn) {
  const ref = useRef(fn);
  ref.current = fn;
  const stable = useRef((...args) => ref.current(...args));
  return stable.current;
}
